// src/components/AgentModal.jsx
import { useState, useEffect, useRef, lazy, Suspense } from 'react'
import { useDeliberateBackdropDismiss } from '../hooks/useDeliberateBackdropDismiss.js'
import { DAYS } from '../lib/schedule.js'
import { TimeInput } from './TimeInput.jsx'
import { DateInput } from './DateInput.jsx'
// Lazy, NOT a static import (2026-07-17 code split): this was the one link that dragged
// StatsTab — and with it the whole recharts library — into the initial bundle via
// IntakeTab → AgentsTab → AgentModal. The summary loads when a modal actually shows it.
const AgentStatsSummary = lazy(() => import('./StatsTab.jsx').then(m => ({ default: m.AgentStatsSummary })))
import { t } from '../lib/i18n.js'
import { toDateStr } from '../lib/attendance.js'
import { fmtPhone, planDaysLeft, findPhoneMatches, findIdMatches } from '../lib/phone.js'
import { MarkLeftDialog } from './MarkLeftDialog.jsx'
import { LogRow } from './AgentLogTab.jsx'
import { getLogs, logEvent } from '../lib/agentLog.js'
import { revertCandidateHire, releaseAgentDrafts, getAppUsers, getPhoneFiles } from '../db/index.js'
import { EsimQrButton } from './settings/EsimFiles.jsx'
import { lockLoginReported, reportAgentSyncFailure } from '../hooks/useSchedule.js'
// The agency-signatory grant lives in CORE (party_capability), not in the scheduler's own
// tables — one permission system, the one that already carries represent_property.
import { getAgencySignatories, setAgencySignatory, getAgentPublicProfiles, setAgentPublicProfilePublication } from '../db/core.js'
import { can } from '../lib/permissions.js'
import { Icon } from './Icon.jsx'
import { COUNTER, pill } from './desk/primitives.jsx'
// ⚠️ TODAY COMES FROM THE OFFICE CLOCK, NEVER THE DEVICE (owner 2026-09-02: "make sure
// system dont use pcs time it used georgia tbilisi local"). `todayStr()` formats the
// SERVER-corrected instant in the office timezone, so neither a PC set to another country nor a
// PC whose clock is simply wrong can decide what day it is here.
import { todayStr } from '../lib/clock.js'


const EMPTY_SLOT = { start: '', end: '', location: 'office' }
const PRIVILEGED_AGENT_ROLES = new Set(['manager', 'admin', 'agent +hr', 'agent+hr', 'general manager'])

function isPrivilegedAgentRole(role) {
  return PRIVILEGED_AGENT_ROLES.has(String(role || '').trim().replace(/\s+/g, ' ').toLowerCase())
}

function generatePassword() {
  const lower = 'abcdefghijkmnpqrstuvwxyz'
  const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ'
  const digits = '23456789'
  const symbols = '!@#$%&*'
  const all = lower + upper + digits + symbols
  const pick = chars => chars[Math.floor(Math.random() * chars.length)]
  const chars = [pick(lower), pick(upper), pick(digits), pick(symbols)]
  while (chars.length < 8) chars.push(pick(all))
  for (let i = chars.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[chars[i], chars[j]] = [chars[j], chars[i]]
  }
  return chars.join('')
}

function buildInitialSchedule(agent) {
  const s = {}
  DAYS.forEach(d => {
    const raw = agent?.schedule?.[d]
    if (!raw) { s[d] = [] }
    else if (Array.isArray(raw)) { s[d] = raw.map(slot => ({ ...slot })) }
    else { s[d] = (!raw.start || raw.location === 'off') ? [] : [{ ...raw }] }
  })
  return s
}

function SlotRow({ slot, onChange, onRemove, canRemove }) {
  const timeClass = 'rounded-lg border border-input px-2 py-1.5 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring w-full'
  return (
    <div className="flex items-center gap-1.5">
      <TimeInput value={slot.start} onChange={v => onChange({ ...slot, start: v })} className={timeClass} />
      <span className="text-muted-foreground/50 text-xs shrink-0">–</span>
      <TimeInput value={slot.end} onChange={v => onChange({ ...slot, end: v })} className={timeClass} />
      <select value={slot.location} onChange={e => onChange({ ...slot, location: e.target.value })}
        className="rounded-lg border border-input px-1.5 py-1.5 text-xs bg-background focus:outline-none focus:ring-2 focus:ring-ring shrink-0">
        <option value="office">{t('location_office')}</option>
        <option value="home">{t('location_remote')}</option>
      </select>
      {canRemove
        ? <button onClick={onRemove}
            className="shrink-0 w-5 h-5 flex items-center justify-center rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors text-sm"
            title={t('agent_modal_remove_slot')}>×</button>
        : <div className="w-5 shrink-0" />}
    </div>
  )
}

const TBILISI_DISTRICTS_FALLBACK = ['ვაკე', 'საბურთალო', 'ისანი', 'სამგორი', 'გლდანი', 'ნაძალადევი', 'დიდუბე', 'ჩუღურეთი', 'მთაწმინდა', 'კრწანისი']

function Lbl({ children, required }) {
  return (
    <label className="text-xs font-medium text-muted-foreground block mb-1">
      {children}{required && <span className="text-destructive ml-0.5">*</span>}
    </label>
  )
}

// The agent modal's deals tab lived here until 2026-08-29 (AgentDeals): a manual form over
// the legacy `deals` table. Removed on owner ask — the table held zero rows and this form
// was its only reader, while the real money already flowed through closed viewings. The
// figures moved into StatsTab's AgentStatsSummary. Do not resurrect a second, hand-keyed
// income source beside the pipeline; two numbers for one agent is worse than none.
// Activity log for one agent — hires, profile/phone changes, schedule, resign,
// plus free-form notes. Read-only history with a quick note adder.
function AgentTimeline({ agentId, agentName }) {
  const [logs,    setLogs]    = useState([])
  const [loading, setLoading] = useState(true)
  const [note,    setNote]    = useState('')
  const [saving,  setSaving]  = useState(false)

  const refresh = async () => { setLoading(true); setLogs(await getLogs(agentId)); setLoading(false) }
  useEffect(() => { refresh() }, [agentId])

  async function addNote() {
    const m = note.trim()
    if (!m) return
    setSaving(true)
    try { await logEvent(agentId, agentName, 'info', m); setNote(''); await refresh() }
    finally { setSaving(false) }
  }

  return (
    <div className="space-y-3">
      {loading ? (
        <div className="py-6 text-center text-muted-foreground text-sm">{t('stats_loading')}</div>
      ) : logs.length === 0 ? (
        <div className="py-6 text-center text-muted-foreground text-sm">{t('log_no_events_long')}</div>
      ) : (
        <div className="divide-y divide-border/50">
          {logs.map(entry => <LogRow key={entry.id} entry={entry} showAgent={false} />)}
        </div>
      )}
      <div className="flex gap-2 pt-1">
        <input value={note} onChange={e => setNote(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addNote() } }}
          {...{placeholder: t('log_message_placeholder')}}
          className="flex-1 rounded-lg border border-input px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring" />
        <button onClick={addNote} disabled={saving || !note.trim()}
          className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 disabled:opacity-50">
          {saving ? '…' : '+'}
        </button>
      </div>
    </div>
  )
}

export function AgentModal({ viewerRole, myOfficeId = null, agent, offices, config = {}, phones = [], onSave, onDelete, onClose,
                             showRevertHire = false, candidateId, preHireStatus, allAgents = [] }) {
  const backdropDismiss = useDeliberateBackdropDismiss(onClose)
  const isEdit = Boolean(agent?.id)
  // Linking an agent to a scheduler login (admin/manager identity) is an admin-only
  // structural decision, NOT self-service — RLS here is authenticated_all (see
  // permissions.js header), so this UI gate is the only thing stopping a manager from
  // relinking any agent's identity to any login, including the admin's. Also skip the
  // app_users fetch entirely for non-admins rather than just hiding the rendered field.
  // (viewerRole = the LOGGED-IN USER's permission role; unrelated to `role` below, which
  // is this agent's own job title state.)
  const canLinkLogin = can(viewerRole, 'agents.link_login')
  // 🔒 WHO MAY SIGN A CONTRACT FOR SENTAVUS (owner ruling 2026-09-12). Admin only — not
  // `agents.link_login` (that one general managers hold too), because this decides who the
  // company's signature belongs to. The switch writes a GRANT in core's `party_capability`, the
  // same table representation has used since 0233; the server refuses the write for anyone but
  // an admin, so this gate is the one that keeps the control off the screen, never the one that
  // protects it.
  const canSignGrant = viewerRole === 'admin' && isEdit
  const [signOn, setSignOn] = useState(null)      // null = not read yet
  const [signBusy, setSignBusy] = useState(false)
  const [signErr, setSignErr] = useState('')
  useEffect(() => {
    if (!canSignGrant || !agent?.id) return
    let live = true
    getAgencySignatories()
      .then(r => { if (live) setSignOn((r?.signatories || []).some(s => s.scheduler_agent_id === agent.id)) })
      .catch(() => { if (live) setSignOn(false) })
    return () => { live = false }
  }, [canSignGrant, agent?.id])
  async function toggleSign(on) {
    setSignBusy(true); setSignErr('')
    try {
      await setAgencySignatory({ schedulerAgentId: agent.id, on, titleKa: role || null, nameKa: name || null, nameEn: name || null })
      setSignOn(on)
    } catch (e) {
      // The server's own word is the useful part: an agent who has never acted in core has no
      // party to grant to, and that is a fact about them rather than a failure of the switch.
      setSignErr(e?.message === 'agent_has_no_core_party' ? t('agent_sign_no_party') : (e?.message || t('agent_sign_failed')))
    } finally { setSignBusy(false) }
  }
  // 🌐 PUBLIC PROFILE ON SENTAVUS.COM (owner ruling 2026-09-23, core 2113). A new agent's profile is created hidden
  // and publishes itself 14 days after they join unless an admin decides otherwise — this is where they decide.
  // Admin/owner only, same as the server's check (which is the real gate; this one keeps the control off the screen).
  const canPublishProfile = ['admin', 'owner'].includes(viewerRole) && isEdit
  const [pubProfile, setPubProfile] = useState(undefined)   // undefined = not read yet, null = no profile row
  const [pubBusy, setPubBusy] = useState(false)
  const [pubErr, setPubErr] = useState('')
  useEffect(() => {
    if (!canPublishProfile || !agent?.id) return
    let live = true
    getAgentPublicProfiles()
      .then(r => { if (live) setPubProfile((r?.items || []).find(p => p.agent_id === agent.id) || null) })
      .catch(() => { if (live) setPubProfile(null) })
    return () => { live = false }
  }, [canPublishProfile, agent?.id])
  async function decidePublication(publish) {
    setPubBusy(true); setPubErr('')
    try {
      const out = await setAgentPublicProfilePublication(agent.id, publish)
      setPubProfile(p => ({ ...(p || { agent_id: agent.id }), public: out?.public_enabled === true, auto_publish_at: null }))
    } catch (e) {
      setPubErr(e?.message || t('agent_sign_failed'))
    } finally { setPubBusy(false) }
  }
  const canAssignPrivilegedRole = can(viewerRole, 'agents.assign_privileged_role')
  // manager/agent_hr are locked to their OWN office app-wide (App.jsx header picker) — must
  // not be able to reassign ANY agent's office from here, or they could move people into/out
  // of that lock themselves. general_manager keeps this one (ops decision, not identity).
  const canChangeOffice = can(viewerRole, 'agents.change_office')
  // ── A manager manages ONLY their own office (owner rule 2026-08-19) ──────────────────
  // ONE definition, used by BOTH the phone list's sort and its options — the sort has to
  // agree with the disable or locked numbers scatter through the free ones.
  //
  // `canChangeOffice` is the existing "not locked to one office" cap (admin +
  // general_manager). For everyone else a number already assigned to ANOTHER office's
  // agent is off limits: taking it would strip a working number from a colleague in an
  // office this viewer does not manage.
  //
  // ⚠️ FAILS CLOSED — allowed only on POSITIVE proof of the same office, so a null office
  // on either side locks rather than opens. Same asymmetry as the server's isMgmtForStaff:
  // over-locking is a papercut, handing away another office's number is not.
  const isForeignPhone = p => !canChangeOffice
    && !!p.agent_id && p.agent_id !== agent?.id
    && !(myOfficeId && p.agentOfficeId && p.agentOfficeId === myOfficeId)
  // manager/agent_hr can now SEE resigned agents scoped to their own role tenure (AgentsTab),
  // but must not edit/reactivate them or change any of their info — only admin/general_manager
  // have full write access to a resigned record. Wraps the whole form in a native
  // <fieldset disabled> below (blocks every input/select/button inside in one shot) rather
  // than gating each field individually.
  const readOnlyLeft = agent?.is_active === false && viewerRole !== 'admin' && viewerRole !== 'general_manager'

  const [name,          setName]          = useState(agent?.name          || '')
  const [role,          setRole]          = useState(agent?.role          || 'Agent')
  const configuredRoles = config.agent_roles?.length
    ? config.agent_roles
    : ['Agent', 'Senior Agent', 'Junior Agent', 'Manager', 'Admin']
  // Managers never receive privileged roles as selectable choices. If they open an
  // existing privileged employee, keep that current value visible (disabled) so the
  // controlled select remains honest while still allowing a deliberate downgrade.
  const visibleRoles = canAssignPrivilegedRole
    ? configuredRoles
    : configuredRoles.filter(r => !isPrivilegedAgentRole(r) || r === role)
  // Per-role default commission %, with an overridable agent value.
  const roleDefaultComm = r => {
    const v = config.role_commissions?.[r]
    return v == null ? '' : String(v)
  }
  const [commission, setCommission] = useState(
    agent?.commission != null ? String(agent.commission) : roleDefaultComm(agent?.role || 'Agent'))
  // Treat an existing agent value that differs from its role default as a manual
  // override, so changing the role won't clobber it. New agents auto-fill.
  const commTouched = useRef(agent?.commission != null && String(agent.commission) !== roleDefaultComm(agent?.role || 'Agent'))
  // ─── Manager team override (owner ask, 2026-08-29) ──────────────────────────
  // A SECOND rate, shown only for managers: their cut of every deal closed by anyone in
  // their office. It is additive — the field above stays exactly what it was, the closing
  // agent's income does not move. Payout maths + the "team = office" reasoning live in
  // lib/performance.js (managerLinksForDeal / managerDealIncome); this is only the input.
  //
  // Matched on the WORD "manager" rather than the exact string 'Manager', because
  // config.agent_roles is admin-editable free text — "General Manager", "Office Manager"
  // and a translated variant all have to reach the field, or the number is unreachable for
  // a role the admin invented an hour ago.
  const isManagerRole = r => /manager/i.test(r || '')
  const [commissionManager, setCommissionManager] = useState(
    agent?.commission_manager != null ? String(agent.commission_manager) : '')
  function changeRole(r) {
    if (!canAssignPrivilegedRole && isPrivilegedAgentRole(r) && r !== agent?.role) return
    setRole(r)
    if (!commTouched.current) setCommission(roleDefaultComm(r))
    // Leaving a manager role CLEARS the override rather than hiding it. lib/performance.js
    // pays on the number being set, not on the role string (a role rename must not stop
    // payroll), so a hidden leftover number would keep paying a demoted manager forever.
    if (!isManagerRole(r)) setCommissionManager('')
  }
  function changeCommission(v) { commTouched.current = true; setCommission(v) }
  // Locked-office viewers hiring a NEW agent (no existing office_id to inherit) default to
  // their own office rather than blank — the field is disabled below, so this is the only
  // chance to set it correctly; otherwise every agent they hire would land unassigned.
  const [officeId,      setOfficeId]      = useState(agent?.office_id || (!canChangeOffice ? (myOfficeId || '') : ''))
  const [email,         setEmail]         = useState(agent?.email         || '')
  const [phoneWork,     setPhoneWork]     = useState(agent?.phone_work    || '')
  const [phonePersonal, setPhonePersonal] = useState(agent?.phone_personal|| '')
  // eSIM QR files (scheduler 0038) — VIEW only here; upload/replace lives in Admin → Work Phone Numbers.
  // RLS returns only the numbers this viewer may see, so a missing button means no access or no file.
  const [phoneFiles,    setPhoneFiles]    = useState([])
  useEffect(() => { if (phones.length) getPhoneFiles().then(setPhoneFiles).catch(() => {}) }, [phones.length])
  const workPhoneRow = phones.find(p => p.number === phoneWork) || null
  // NEW agent → today, pre-filled and editable (owner ask, 2026-08-29). An EXISTING agent
  // with no hire_date stays blank on purpose: today is not when they were hired, and quietly
  // filling it would fabricate a date onto a historical record the moment someone opened it.
  // db/index.js addAgent stamps the same default, so the two creation paths agree even if a
  // caller never opens this form.
  const [hireDate,      setHireDate]      = useState(agent?.hire_date || (agent ? '' : todayStr()))
  const [dob,           setDob]           = useState(agent?.dob           || '')
  const [idNumber,      setIdNumber]      = useState(agent?.id_number     || '')
  const [loginId,       setLoginId]       = useState(agent?.login_id      || '')
  const [loginPass,     setLoginPass]     = useState(agent?.login_pass    || '')
  // Scheduler login link (app_users) — separate identity from the property-DB login above.
  // Optional; only managers/admins typically have a scheduler login at all.
  const [authUserId,    setAuthUserId]    = useState(agent?.auth_user_id  || '')
  const [appUsers,      setAppUsers]      = useState([])
  useEffect(() => { if (canLinkLogin) getAppUsers().then(setAppUsers).catch(() => setAppUsers([])) }, [canLinkLogin])
  const [district,      setDistrict]      = useState(agent?.district      || '')
  const [address,       setAddress]       = useState(agent?.address       || '')
  const [languages,     setLanguages]     = useState(() => Array.isArray(agent?.languages) ? agent.languages : [])
  const [isActive,      setIsActive]      = useState(agent?.is_active !== false)
  const [resignDate,    setResignDate]    = useState(agent?.resign_date   || '')
  const [resignReason,  setResignReason]  = useState(agent?.resign_reason || '')
  const [schedule,      setSchedule]      = useState(() => buildInitialSchedule(agent))
  const [saving,        setSaving]        = useState(false)
  const [nameErr,       setNameErr]       = useState('')
  const [tab,           setTab]           = useState('info')
  const [showPass,      setShowPass]      = useState(false)
  const [showEmployment, setShowEmployment] = useState(false)
  const [showLogin,      setShowLogin]      = useState(false)
  const [revertSecs,    setRevertSecs]    = useState(() => {
    if (!showRevertHire || !candidateId) return 0
    try {
      const stored = localStorage.getItem(`hire_ts_${candidateId}`)
      if (!stored) return 300
      const elapsed = Math.floor((Date.now() - Number(stored)) / 1000)
      const remaining = 300 - elapsed
      return remaining > 0 ? remaining : 0
    } catch { return 0 }
  })
  const [reverting,     setReverting]     = useState(false)
  const [showMarkLeft,  setShowMarkLeft]  = useState(false)
  const revertTimerRef = useRef(null)
  const dirtyRef       = useRef(false)  // set to true when user edits anything

  // Start revert countdown
  useEffect(() => {
    if (!showRevertHire) return
    revertTimerRef.current = setInterval(() => {
      setRevertSecs(s => {
        if (s <= 1) {
          clearInterval(revertTimerRef.current)
          try { localStorage.removeItem(`hire_ts_${candidateId}`); localStorage.removeItem(`hire_ts_agent_${agent?.id}`) } catch {}
          return 0
        }
        return s - 1
      })
    }, 1000)
    return () => clearInterval(revertTimerRef.current)
  }, [showRevertHire])

  async function handleRevertHire() {
    if (!candidateId || !agent?.id) return
    if (!confirm(t('cand_hire_revert_confirm').replace('{name}', agent.name))) return
    clearInterval(revertTimerRef.current)
    setReverting(true)
    try {
      await revertCandidateHire(candidateId, agent.id, preHireStatus)
      try { localStorage.removeItem(`hire_ts_${candidateId}`); localStorage.removeItem(`hire_ts_agent_${agent?.id}`) } catch {}
      onClose('revert')
    } finally { setReverting(false) }
  }

  useEffect(() => {
    const h = e => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [onClose])

  function updateSlot(day, idx, updated) {
    setSchedule(prev => { const s = [...prev[day]]; s[idx] = updated; return { ...prev, [day]: s } })
  }
  function addSlot(day) {
    setSchedule(prev => ({ ...prev, [day]: [...prev[day], { ...EMPTY_SLOT }] }))
  }
  function removeSlot(day, idx) {
    setSchedule(prev => ({ ...prev, [day]: prev[day].filter((_, i) => i !== idx) }))
  }
  function setDayOff(day, isOff) {
    const office = offices.find(o => o.id === officeId)
    // Weekend days default to the office's weekend hours (falling back to weekday).
    const weekend = day === 'Sat' || day === 'Sun'
    const start = (weekend && office?.weekend_work_start) || office?.work_start || '09:00'
    const end   = (weekend && office?.weekend_work_end)   || office?.work_end   || '18:00'
    setSchedule(prev => ({
      ...prev,
      [day]: isOff ? [] : [{ start, end, location: 'office' }],
    }))
  }

  function buildPayload(overrides = {}) {
    return {
      name: name.trim(), role, office_id: officeId || null,
      email, phone_work: phoneWork, phone_personal: phonePersonal,
      hire_date: hireDate || null, dob: dob || null,
      commission: commission === '' ? null : Number(commission),
      // Never persisted for a non-manager — see changeRole. Sent explicitly as null rather
      // than omitted, so demoting someone actually clears the stored number.
      commission_manager: (!isManagerRole(role) || commissionManager === '') ? null : Number(commissionManager),
      id_number: idNumber || null,
      login_id: (loginId || '').toLowerCase().trim() || null, login_pass: loginPass || null,
      auth_user_id: authUserId || null,
      district: district || null, address: address || null,
      languages: languages.length > 0 ? languages : null,
      is_active: isActive,
      resign_date: resignDate || null,
      resign_reason: resignReason || null,
      schedule,
      ...overrides,
    }
  }

  async function handleSave() {
    if (!name.trim()) { setNameErr(t('agent_modal_name_err')); return }
    if (loginOwner) { setTab('info'); setShowLogin(true); return }
    // Defense in depth for a forged DOM event. The database trigger is the actual
    // security boundary; this keeps the client from even attempting the write.
    if (!canAssignPrivilegedRole && isPrivilegedAgentRole(role) && role !== agent?.role) return
    const today = todayStr()
    // Typing a past/today resign date and hitting plain Save used to skip the
    // confirmation dialog entirely (only the dedicated "Fire" button showed it) —
    // route both through the SAME dialog so number/cards/login-lock are always a
    // deliberate choice, never a silent side effect of editing a date field.
    const wasResigned = agent?.is_active === false
    // ⚠️ A TRANSITION, NOT A STATE. `resignDate <= today` alone asks "does this person have a past
    // resign date", which is true for anyone reinstated while the date was left behind — so every
    // later edit re-opened this dialog and offered to fire them again. Firing is something you
    // DO, so the test is that the date CHANGED in this edit. Belt to the braces of the reinstate
    // fix in AgentsTab: even if a stale date reaches an active agent by some other route, editing
    // their address will not propose ending their employment.
    const dateIsNew = resignDate !== (agent?.resign_date || '')
    if (!wasResigned && dateIsNew && resignDate && resignDate <= today) { setShowMarkLeft(true); return }
    setSaving(true)
    try {
      await onSave(buildPayload())
      onClose()
    } finally { setSaving(false) }
  }

  // Replaces hard delete: marks the agent as resigned (inactive) instead of removing
  // them. Opens the app-styled dialog so the admin can also free the work number.
  function handleMarkLeft() { setShowMarkLeft(true) }

  // Review 2026-09-23: save FIRST, then lock/release — they used to fire before the save,
  // so a failed save left a locked login on an agent the roster still calls active. A
  // failed save now keeps the modal open and says so. Firing NOW records today when the
  // stored resign date is still in the future.
  async function confirmMarkLeft({ removeNumber, moveCards, lockLogin }) {
    const today = todayStr()
    setShowMarkLeft(false)
    setSaving(true)
    try {
      try {
        await onSave(buildPayload({
          is_active: false,
          resign_date: (resignDate && resignDate <= today) ? resignDate : today,
          ...(removeNumber ? { phone_work: '' } : {}),
        }))
      } catch (e) { reportAgentSyncFailure(name, e); return }
      if (moveCards && agent?.login_id) {
        releaseAgentDrafts(agent.login_id).catch(e => reportAgentSyncFailure(name, e))
      }
      if (lockLogin && agent?.login_id) await lockLoginReported(agent.login_id, true, name)
      onClose()
    } finally { setSaving(false) }
  }

  // ⚠️ DUPLICATE GUARD (owner ask, 2026-08-29). On 2026-08-28 an agent hired through the
  // candidate pipeline was added a SECOND time by hand three hours later, because her existing
  // record had no office and is therefore invisible to every manager — the person adding her
  // had no way to know she was already there. Merging the two afterwards took a migration that
  // re-pointed eight cascade relationships and a cross-database bridge.
  //
  // It warns, it does not block: two agents can legitimately share a landline, and a guard that
  // refuses a real entry gets worked around. It matches on the NORMALISED number, because the
  // two records held the same phone as '+995 592 79 15 99' and '592791599' — a string compare
  // finds nothing. See lib/phone.js phoneKey.
  //
  // `allAgents` is the FULL roster on purpose. Handing this the office-filtered list would hide
  // exactly the record the person cannot see, which is the situation that created the duplicate.
  // Personal phone and national ID only — never the work number, which is a company SIM
  // that rotates between agents by design. See lib/phone.js.
  const phoneDupes = findPhoneMatches([phonePersonal], allAgents, agent?.id)
  const idDupes    = findIdMatches(idNumber, allAgents, agent?.id)
  // A login id IS an account (`<login_id>@sentavus.com`), so two agents carrying the same one
  // share one account: saving either card's password changes the other's, and a resigned
  // agent's lock locks the newcomer out. Measured 2026-10-10 — Mariam Mchedlishvili was given
  // `mariamm` (Mariam Meladze, resigned and locked) and Barbare Kevekhishvili `barbarek`
  // (ბარბარე კოკილაშვილი, active). Checked against the FULL roster, resigned agents included,
  // because a resigned agent's account still exists and is locked. Blocks the save outright.
  const loginOwner = loginId
    ? allAgents.find(a => a.id !== agent?.id && (a.login_id || '').toLowerCase().trim() === loginId)
    : null

  const inp = 'w-full rounded-lg border border-input px-3 py-2 text-base sm:text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring'

  const SUB_TABS = [
    ['info', t('agent_modal_info')],
    ['schedule', t('agent_modal_schedule')],
    // 'deals' removed 2026-08-29 (owner). Its figures now live in the stats tab, sourced
    // from closed viewings instead of the empty legacy `deals` table. See AgentStatsSummary.
    ...(isEdit ? [['stats', t('stats_tab_label')], ['timeline', t('agent_modal_timeline')]] : []),
  ]

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-3 sm:p-4"
      {...backdropDismiss}>
      <div className="bg-card border border-border rounded-xl w-full max-w-lg max-h-[92vh] flex flex-col shadow-xl overflow-hidden">

        {/* shrink-0 on the header and the tab bar (owner-reported 2026-09-29): in this height-capped
            column the tab bar was allowed to SHRINK, so a long form squeezed it to half height — the
            tab labels were clipped and the fields slid over them. Only the body below may scroll. */}
        <div className="flex items-center justify-between px-4 py-4 border-b border-border shrink-0">
          <h2 className="text-base font-semibold">{isEdit ? `${agent.name}` : t('agent_modal_add')}</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground text-xl leading-none">&times;</button>
        </div>

        <div className="flex border-b border-border px-4 overflow-x-auto overflow-y-hidden no-scrollbar shrink-0" style={{ scrollbarWidth: 'none' }}>
          {SUB_TABS.map(([id, label]) => (
            <button key={id} onClick={() => setTab(id)}
              className={`py-2.5 px-3 text-xs font-medium border-b-2 -mb-px transition-colors ${tab === id ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
              {label}
            </button>
          ))}
        </div>

        <div className="overflow-y-auto px-4 py-4 space-y-5 flex-1 min-h-0">
        <fieldset disabled={readOnlyLeft} className="contents">
          {tab === 'info' && (
            <>
              {/* Basic */}
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/50 mb-2">Basic</div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <Lbl required>{t('agent_modal_name')}</Lbl>
                    <input value={name} onChange={e => { setName(e.target.value); setNameErr('') }}
                      className={`${inp} ${nameErr ? 'border-destructive' : ''}`} />
                    {nameErr && <p className="text-xs text-destructive mt-1">{nameErr}</p>}
                  </div>
                  <div>
                    <Lbl>{t('agent_modal_role')}</Lbl>
                    <select value={role} onChange={e => changeRole(e.target.value)} className={inp}>
                      {visibleRoles.map(r => (
                        <option key={r} disabled={!canAssignPrivilegedRole && isPrivilegedAgentRole(r)}>{r}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Lbl>{t('agent_modal_office')}</Lbl>
                    <select value={officeId} onChange={e => setOfficeId(e.target.value)} disabled={!canChangeOffice}
                      className={`${inp} ${!canChangeOffice ? 'opacity-60 cursor-not-allowed' : ''}`}>
                      <option value="">{t('agent_modal_unassigned')}</option>
                      {offices.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                    </select>
                  </div>
                  <div className="col-span-2">
                    <Lbl>{t('agent_modal_email')}</Lbl>
                    <input value={email} onChange={e => setEmail(e.target.value)}
                      {...{placeholder: t('agent_modal_email_placeholder')}} type="email" className={inp} />
                  </div>
                  <div>
                    <Lbl>{t('agent_modal_phone_work')}</Lbl>
                    {phones.length > 0 ? (
                      <div className="flex items-center gap-1.5">
                      <select value={phoneWork} onChange={e => {
                        const num = e.target.value
                        const taken = phones.find(p => p.number === num && p.agent_id && p.agent_id !== agent?.id)
                        if (taken) {
                          const confirmed = window.confirm(t('agent_modal_phone_reassign').replace('{num}', num).replace('{agentName}', agent?.name || '?'))
                          if (!confirmed) return
                        }
                        setPhoneWork(num)
                      }} className={`${inp} min-w-0 flex-1`}>
                        <option value="">— select number —</option>
                        {[...phones]
                          .sort((a, b) => {
                            const rank = p => (p.banned || isForeignPhone(p)) ? 2 : p.agent_id ? 1 : 0
                            const r = rank(a) - rank(b)
                            if (r) return r
                            // Among free numbers, surface the ones with most plan days left
                            if (rank(a) === 0) {
                              const da = planDaysLeft(a.plan_started_at), db = planDaysLeft(b.plan_started_at)
                              const va = da == null ? -Infinity : da, vb = db == null ? -Infinity : db
                              if (vb !== va) return vb - va
                            }
                            return a.number.localeCompare(b.number)
                          })
                          .map(p => {
                            const isMine  = p.agent_id === agent?.id
                            const isTaken = p.agent_id && !isMine
                            // `agentName` resolves against the FULL roster (AgentsTab passes
                            // allAgents), not the office-filtered one. It used to resolve
                            // against the filtered roster, so every number held by another
                            // office's agent rendered as a bare '?' — the holder was hidden
                            // from the one person who needed to know who to go ask.
                            const owner   = p.agentName || '?'
                            // ── A manager manages ONLY their own office (owner rule 2026-08-19) ──
                            // `canChangeOffice` is the existing "not locked to one office" cap
                            // (admin + general_manager). For everyone else a number already
                            // assigned to another office's agent is off limits: taking it would
                            // strip a working number from a colleague in an office this viewer
                            // does not manage. Disabled like `banned` — visible and explained,
                            // never hidden, so the roster stays honest about what exists.
                            //
                            // ⚠️ FAILS CLOSED: allowed only on POSITIVE proof of the same office.
                            // A null office on either side is not proof and therefore locks. Same
                            // asymmetry as the server's isMgmtForStaff — over-locking is a
                            // papercut, handing away another office's number is not.
                            const foreignOffice = isForeignPhone(p)
                            let label = p.number
                            if (p.banned) label += ' — banned'
                            else {
                              if (isMine)       label += ' ✓ (mine)'
                              else if (isTaken) label += ` — ${owner}`
                              else if (p.note)  label += ` — ${p.note}`
                              const dl = planDaysLeft(p.plan_started_at)
                              if (dl != null)      label += dl > 0 ? `  ·  ${dl}d left` : '  ·  plan expired'
                              else if (!isTaken)   label += '  ·  no plan'
                              // Name the office that holds it, so a locked row reads as
                              // "someone else's" rather than as an unexplained dead entry. No
                              // t() key here on purpose: a missing translation renders the raw
                              // key, and the office name is already the clearer label anyway.
                              const off = foreignOffice ? offices.find(o => o.id === p.agentOfficeId) : null
                              if (off) label += `  ·  ${off.name}`
                            }
                            return (
                              <option key={p.id} value={p.number} disabled={p.banned || foreignOffice}>
                                {label}
                              </option>
                            )
                          })}
                      </select>
                      {workPhoneRow && (
                        <span className="shrink-0">
                          <EsimQrButton phone={workPhoneRow} files={phoneFiles.filter(f => f.phone_number_id === workPhoneRow.id)} />
                        </span>
                      )}
                      </div>
                    ) : (
                      <input value={phoneWork} onChange={e => setPhoneWork(e.target.value)}
                        {...{placeholder: t('agent_modal_work_phone_ph')}} className={inp} />
                    )}
                  </div>
                  {phoneDupes.length > 0 && (
                    <div className="col-span-2 -mt-1 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-400">
                      <Icon name="alert-triangle" size={12} /> {t('agent_dupe_phone')}{' '}
                      <span className="font-medium">{phoneDupes.map(a2 => a2.name).join(', ')}</span>
                      <div className="text-amber-700/70 dark:text-amber-400/70 mt-0.5">{t('agent_dupe_phone_hint')}</div>
                    </div>
                  )}
                  <div>
                    <Lbl>{t('agent_modal_phone_personal')}</Lbl>
                    <input value={phonePersonal} onChange={e => setPhonePersonal(e.target.value)}
                      {...{placeholder: t('agent_modal_personal_phone_ph')}} className={inp} />
                  </div>
                </div>
              </div>

              {/* Identity */}
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/50 mb-2">{t('admin_agent_section_identity')}</div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Lbl>{t('agent_modal_dob')}</Lbl>
                    <DateInput value={dob} onChange={setDob}
                      className="w-full rounded-lg border border-input px-2 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring" />
                    {dob && (
                      <div className="text-xs text-muted-foreground mt-1">
                        Age: {Math.floor((Date.now() - new Date(dob).getTime()) / (365.25 * 24 * 3600 * 1000))}
                      </div>
                    )}
                  </div>
                  {idDupes.length > 0 && (
                    <div className="col-span-2 -mb-1 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-600 dark:text-red-400">
                      <Icon name="alert-triangle" size={12} /> {t('agent_dupe_id')}{' '}
                      <span className="font-medium">{idDupes.map(a2 => a2.name).join(', ')}</span>
                      <div className="text-red-600/70 dark:text-red-400/70 mt-0.5">{t('agent_dupe_id_hint')}</div>
                    </div>
                  )}
                  <div>
                    <Lbl>{t('agent_modal_id_number')}</Lbl>
                    <input value={idNumber} onChange={e => setIdNumber(e.target.value)}
                      {...{placeholder: t('agent_modal_id_placeholder')}} className={inp} />
                  </div>
                </div>
              </div>

              {/* Address */}
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/50 mb-2">Address</div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Lbl>{t('agent_modal_section_district')}</Lbl>
                    <select value={district} onChange={e => setDistrict(e.target.value)} className={inp}>
                      <option value="">— select —</option>
                      {(config.districts?.length ? config.districts : TBILISI_DISTRICTS_FALLBACK).map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </div>
                  <div>
                    <Lbl>{t('agent_modal_section_address')}</Lbl>
                    <input value={address} onChange={e => setAddress(e.target.value)}
                      {...{placeholder: t('agent_modal_address_placeholder')}} className={inp} />
                  </div>
                </div>
              </div>

              {/* Languages */}
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/50 mb-2">{t('agent_modal_section_languages')}</div>
                <div className="flex flex-wrap gap-2">
                  {(config.languages?.length ? config.languages : ['Georgian','English','Russian']).map(lang => {
                    const checked = languages.includes(lang)
                    return (
                      <button key={lang} type="button"
                        onClick={() => setLanguages(prev => checked ? prev.filter(l => l !== lang) : [...prev, lang])}
                        className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${
                          checked ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground hover:border-primary/40'
                        }`}>
                        {lang}
                      </button>
                    )
                  })}
                </div>
              </div>

              {/* Employment */}
              <div>
                <button type="button" onClick={() => setShowEmployment(v => !v)}
                  className="w-full flex items-center justify-between text-xs font-semibold uppercase tracking-wider text-muted-foreground/50 mb-2 hover:text-muted-foreground">
                  <span>Employment</span>
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                    className={`transition-transform ${showEmployment ? 'rotate-180' : ''}`}>
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                </button>
                {showEmployment && <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Lbl>{t('agent_modal_hire_date')}</Lbl>
                    <DateInput value={hireDate} onChange={setHireDate}
                      className="w-full rounded-lg border border-input px-2 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring" />
                  </div>
                  <div>
                    <Lbl>{t('agent_modal_commission')}</Lbl>
                    <input type="number" min="0" step="0.1" value={commission}
                      onChange={e => changeCommission(e.target.value)}
                      placeholder={roleDefaultComm(role) || t('role_comm_default')} className={inp} />
                  </div>
                  {/* Managers only. col-span-2 so the help line beneath it has room to say
                      what the number actually does — "second commission %" on its own reads
                      as a personal rate, and the whole point is that it is not one. */}
                  {isManagerRole(role) && (
                    <div className="col-span-2">
                      <Lbl>{t('agent_modal_commission_manager')}</Lbl>
                      <input type="number" min="0" step="0.1" value={commissionManager}
                        onChange={e => setCommissionManager(e.target.value)}
                        placeholder="0" className={inp} />
                      <p className="text-xs text-muted-foreground/70 mt-1">{t('agent_modal_commission_manager_hint')}</p>
                    </div>
                  )}
                  <div>
                    <Lbl>{t('agent_modal_resign_date')}</Lbl>
                    <DateInput value={resignDate} onChange={setResignDate}
                      className="w-full rounded-lg border border-input px-2 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring" />
                    {resignDate && (() => {
                      const today = todayStr()
                      const displayDate = new Date(resignDate + 'T12:00:00').toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
                      if (resignDate > today) {
                        return <p className="text-xs text-amber-600 dark:text-amber-400 mt-1"><Icon name="clock" /> {t('agents_resign_future_toast').replace('{name}', name || '…').replace('{date}', displayDate)}</p>
                      } else {
                        return <p className="text-xs text-red-500 mt-1"><Icon name="alert-triangle" /> {t('agents_resign_past_toast').replace('{name}', name || '…').replace('{date}', displayDate)}</p>
                      }
                    })()}
                  </div>
                  <div className="col-span-2">
                    <Lbl>{t('agent_modal_resign_reason')}</Lbl>
                    <textarea value={resignReason} onChange={e => setResignReason(e.target.value)}
                      {...{placeholder: t('agent_modal_resign_placeholder')}}
                      rows={2} className={`${inp} resize-none`} />
                  </div>
                </div>}
              </div>

              {/* Login credentials */}
              <div>
                <button type="button" onClick={() => setShowLogin(v => !v)}
                  className="w-full flex items-center justify-between text-xs font-semibold uppercase tracking-wider text-muted-foreground/50 mb-2 hover:text-muted-foreground">
                  {/* One pair for BOTH logins: the extension (property DB) and the scheduler
                      app itself (server provisions both + auto-links auth_user_id, 2026-07-05). */}
                  <span>Login credentials (scheduler + extension)</span>
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                    className={`transition-transform ${showLogin ? 'rotate-180' : ''}`}>
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                </button>
                {showLogin && <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Lbl>{t('agent_modal_login_id')}</Lbl>
                    {/* autoComplete off: without it the browser autofills the LOGGED-IN
                        operator's own saved credentials into a new agent's login, which the
                        save path then adopts — how Tako's login got hijacked (2026-07-08).
                        Lowercase-only: the login becomes an auth EMAIL (login@sentavus.com)
                        and GoTrue lowercases emails, so an uppercase login_id can never
                        match the identity coming back from a JWT — that mismatch silently
                        unattributed two agents' entire core history and minted phantom
                        parties (the Lukaab/Lukab/TakoK incident, 2026-07-12). */}
                    <input value={loginId} onChange={e => setLoginId(e.target.value.toLowerCase().trim())}
                      autoComplete="off"
                      {...{placeholder: t('agent_modal_login_id_placeholder')}}
                      className={`${inp} ${loginOwner ? 'border-destructive' : ''}`} />
                    {loginOwner && (
                      <p className="text-xs text-destructive mt-1">
                        {t(loginOwner.is_active === false ? 'agent_modal_login_taken_left' : 'agent_modal_login_taken').replace('{name}', loginOwner.name)}
                      </p>
                    )}
                  </div>
                  <div>
                    <Lbl>{t('agent_modal_login_pass')}</Lbl>
                    <div className="relative">
                      <input
                        type={showPass ? 'text' : 'password'}
                        value={loginPass} onChange={e => setLoginPass(e.target.value)}
                        autoComplete="new-password"
                        placeholder="••••••••" className={`${inp} pr-16`} />
                      <button type="button" onClick={() => { setLoginPass(generatePassword()); setShowPass(true) }}
                        title={t('agent_modal_generate_pass')}
                        className="absolute right-9 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/>
                        </svg>
                      </button>
                      <button type="button" onClick={() => setShowPass(v => !v)}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                        {showPass ? (
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                            <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                            <line x1="1" y1="1" x2="23" y2="23"/>
                          </svg>
                        ) : (
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                            <circle cx="12" cy="12" r="3"/>
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                  {canSignGrant && <div className="col-span-2 rounded-lg border border-border/60 p-2.5 space-y-1">
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={signOn === true} disabled={signOn === null || signBusy}
                        onChange={e => toggleSign(e.target.checked)} data-testid="agent-sign-for-agency" />
                      <span className="font-medium">{t('agent_sign_for_agency')}</span>
                    </label>
                    <p className="text-xs text-muted-foreground">{t('agent_sign_for_agency_help')}</p>
                    {signErr && <p className="text-xs text-destructive" data-testid="agent-sign-error">{signErr}</p>}
                  </div>}
                  {canPublishProfile && <div className="col-span-2 rounded-lg border border-border/60 p-2.5 space-y-1.5" data-testid="agent-public-profile">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="text-sm font-medium">{t('agent_public_profile')}</span>
                      {pubProfile?.public && pubProfile.ref && (
                        <a href={`https://sentavus.com/ka/agents/${pubProfile.ref}`} target="_blank" rel="noopener noreferrer" className="text-xs underline">
                          {t('agent_public_profile_view')}
                        </a>
                      )}
                    </div>
                    {pubProfile === undefined ? (
                      <p className="text-xs text-muted-foreground">…</p>
                    ) : pubProfile === null ? (
                      <p className="text-xs text-muted-foreground">{t('agent_public_profile_none')}</p>
                    ) : (
                      <>
                        <p className="text-sm" data-testid="agent-public-profile-state">
                          {pubProfile.public
                            ? t('agent_public_profile_public')
                            : pubProfile.auto_publish_at
                              ? `${t('agent_public_profile_pending')} ${new Date(pubProfile.auto_publish_at).toLocaleDateString()}`
                              : t('agent_public_profile_hidden')}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {!pubProfile.public && (
                            <button type="button" disabled={pubBusy} onClick={() => decidePublication(true)}
                              className="px-3 py-1.5 rounded-lg border border-border text-xs font-medium hover:border-primary/40 disabled:opacity-50">
                              {t('agent_public_profile_publish_now')}
                            </button>
                          )}
                          {(pubProfile.public || pubProfile.auto_publish_at) && (
                            <button type="button" disabled={pubBusy} onClick={() => decidePublication(false)}
                              className="px-3 py-1.5 rounded-lg border border-border text-xs font-medium hover:border-destructive/50 disabled:opacity-50">
                              {pubProfile.public ? t('agent_public_profile_unpublish') : t('agent_public_profile_block')}
                            </button>
                          )}
                        </div>
                      </>
                    )}
                    <p className="text-xs text-muted-foreground">{t('agent_public_profile_help')}</p>
                    {pubErr && <p className="text-xs text-destructive">{pubErr}</p>}
                  </div>}
                  {canLinkLogin && <div className="col-span-2">
                    <Lbl>{t('agent_modal_scheduler_login')}</Lbl>
                    <select value={authUserId} onChange={e => setAuthUserId(e.target.value)} className={inp}>
                      <option value="">{t('agent_modal_scheduler_login_none')}</option>
                      {appUsers.map(u => <option key={u.auth_user_id} value={u.auth_user_id}>{u.email} ({u.role})</option>)}
                    </select>
                  </div>}
                </div>}
              </div>
            </>
          )}

          {tab === 'schedule' && (
            <div className="space-y-3">
              {DAYS.map((day, di) => {
                const slots = schedule[day]
                const isOff = slots.length === 0
                const isWeekend = di >= 5
                return (
                  <div key={day} className={`rounded-xl border p-3 ${isOff ? 'border-border/40 bg-muted/10' : 'border-border bg-card'}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-bold w-8 ${isWeekend ? 'text-muted-foreground/60' : ''}`}>{t('days')[di] ?? day}</span>
                        {slots.length > 1 && (
                          <span className={`${COUNTER} font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400`}>{t('agent_modal_split')}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {!isOff && slots.length < 3 && (
                          <button onClick={() => addSlot(day)}
                            className="text-xs font-medium text-primary hover:underline">{t('add_slot')}</button>
                        )}
                        <button onClick={() => setDayOff(day, !isOff)}
                          className={pill('none', { door: true, extra: `font-medium transition-colors border-transparent ${
                            isOff ? 'bg-muted text-muted-foreground hover:bg-primary hover:text-primary-foreground'
                                  : 'bg-red-100 text-red-600 dark:bg-red-900/20 dark:text-red-400 hover:bg-red-200'
                          }` })}>
                          {isOff ? t('working_day') : t('agent_modal_day_off')}
                        </button>
                      </div>
                    </div>
                    {!isOff ? (
                      <div className="space-y-2">
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground px-0.5">
                          <div className="flex-1">{t('schedule_start')}</div>
                          <div className="w-3 shrink-0" />
                          <div className="flex-1">{t('schedule_end')}</div>
                          <div className="shrink-0 w-16 text-center">{t('schedule_location')}</div>
                          <div className="w-5 shrink-0" />
                        </div>
                        {slots.map((slot, idx) => (
                          <SlotRow key={idx} slot={slot}
                            onChange={u => updateSlot(day, idx, u)}
                            onRemove={() => removeSlot(day, idx)}
                            canRemove={slots.length > 1} />
                        ))}
                      </div>
                    ) : (
                      <div className="text-xs text-muted-foreground/40 text-center py-1">Day off</div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
          {tab === 'stats' && agent && (
            <Suspense fallback={null}><AgentStatsSummary agent={agent} config={config} viewerRole={viewerRole} /></Suspense>
          )}
          {tab === 'timeline' && agent && (
            <AgentTimeline agentId={agent.id} agentName={agent.name} />
          )}
        </fieldset>
        </div>

        <div className="flex items-center gap-2 px-4 py-4 border-t border-border shrink-0">
          {isEdit && isActive && (
            <button onClick={handleMarkLeft}
              className="text-sm font-medium text-destructive hover:bg-destructive/10 px-3 py-2 rounded-lg transition-colors">
              {t('qa_fire')}
            </button>
          )}
          <div className="flex-1" />
          {showRevertHire && (
            <button onClick={revertSecs > 0 ? handleRevertHire : undefined}
              disabled={reverting || revertSecs === 0}
              title={revertSecs > 0 ? `${t('cand_hire_revert')} (${Math.floor(revertSecs/60)}:${String(revertSecs%60).padStart(2,'0')})` : ''}
              className={`w-9 h-9 rounded-full border-2 flex items-center justify-center transition-all shrink-0 relative ${
                revertSecs > 0
                  ? 'border-amber-400 text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/20 cursor-pointer'
                  : 'border-muted text-muted-foreground/40 cursor-default'
              }`}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                <path d="M3 3v5h5" />
              </svg>
              {revertSecs > 0 && (
                <span className="absolute -top-1.5 -right-1.5 text-[9px] bg-amber-400 text-white rounded-full w-4 h-4 flex items-center justify-center font-bold tabular-nums">
                  {revertSecs > 60 ? `${Math.floor(revertSecs/60)}m` : `${revertSecs}s`}
                </span>
              )}
            </button>
          )}
          {/* no Cancel button — the top-right ✕ is the single close affordance (2026-07-11) */}
          {!readOnlyLeft && (
            <button onClick={handleSave} disabled={saving || !!loginOwner}
              title={loginOwner ? t('agent_modal_login_taken').replace('{name}', loginOwner.name) : undefined}
              className="text-sm font-medium px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:opacity-90 transition-opacity disabled:opacity-60">
              {saving ? t('agent_modal_saving') : isEdit ? t('agent_modal_save') : t('agent_modal_adding')}
            </button>
          )}
        </div>
      </div>

      {showMarkLeft && (
        <MarkLeftDialog
          name={agent.name}
          numberLabel={phoneWork ? fmtPhone(phoneWork) : null}
          loginId={agent?.login_id || null}
          onConfirm={confirmMarkLeft}
          onCancel={() => setShowMarkLeft(false)}
        />
      )}
    </div>
  )
}
